import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { apiGetGeo, apiGetRates, apiGetCurrencies } from '../api/geo';

// Currency definitions and FX rates are loaded from the backend.
// All product prices are stored in INR (the base) — the rates here are
// "1 INR -> X foreign units" multipliers used for display only.
const STORAGE_KEY = 'bookscape_currency';
const RATES_STORAGE_KEY = 'bookscape_fx_rates_v2';
const CURRENCIES_STORAGE_KEY = 'bookscape_currencies_v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const normalizeCurrency = (code) => {
  const normalized = String(code || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : '';
};

const CurrencyContext = createContext(null);

const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.value || !parsed?.savedAt) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify({ value, savedAt: Date.now() }));
  } catch {
    // ignore — private mode / storage full
  }
};

const toCurrencyMap = (list) => {
  const out = {};
  for (const c of list || []) {
    if (c?.code) out[c.code] = c;
  }
  return out;
};

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState(() => {
    const cached = readCache(CURRENCIES_STORAGE_KEY);
    return cached ? toCurrencyMap(cached) : {};
  });
  const [rates, setRates] = useState(() => readCache(RATES_STORAGE_KEY) || null);
  const [currencyCode, setCurrencyCode] = useState(() =>
    normalizeCurrency(localStorage.getItem(STORAGE_KEY))
  );

  // Load active currencies from the backend.
  useEffect(() => {
    let cancelled = false;
    apiGetCurrencies()
      .then((data) => {
        if (cancelled || !Array.isArray(data?.currencies)) return;
        const map = toCurrencyMap(data.currencies);
        if (Object.keys(map).length > 0) {
          setCurrencies(map);
          writeCache(CURRENCIES_STORAGE_KEY, data.currencies);
        }
      })
      .catch(() => {
        // Network failed — bootstrap/cache stays in effect
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load FX rates.
  useEffect(() => {
    if (rates) return;
    let cancelled = false;
    apiGetRates()
      .then((data) => {
        if (cancelled || !data?.rates) return;
        setRates(data.rates);
        writeCache(RATES_STORAGE_KEY, data.rates);
      })
      .catch(() => {
        // ignore
      });
    return () => {
      cancelled = true;
    };
  }, [rates]);

  useEffect(() => {
    const codes = Object.keys(currencies);
    if (codes.length === 0 || currencies[currencyCode]) return undefined;
    let cancelled = false;

    apiGetGeo()
      .then((geo) => {
        if (cancelled) return;
        const detected = normalizeCurrency(geo?.currency);
        const next = currencies[detected] ? detected : codes[0];
        setCurrencyCode(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // Storage is optional; backend data remains authoritative.
        }
      })
      .catch(() => {
        if (!cancelled) setCurrencyCode(codes[0]);
      });
    return () => { cancelled = true; };
  }, [currencies, currencyCode]);

  const setCurrency = useCallback((code) => {
    const validatedCode = normalizeCurrency(code);
    if (!currencies[validatedCode]) return;
    setCurrencyCode(validatedCode);
    try {
      localStorage.setItem(STORAGE_KEY, validatedCode);
    } catch {
      // ignore
    }
  }, [currencies]);

  const value = useMemo(() => {
    const cur = currencies[currencyCode];
    const configuredRate = cur && rates ? Number(rates[cur.code]) : NaN;
    const effectiveRate = Number.isFinite(configuredRate) && configuredRate > 0
      ? configuredRate
      : null;

    const convert = (amount) => effectiveRate == null ? 0 : (Number(amount) || 0) * effectiveRate;

    const convertFrom = (amount, sourceCode = 'INR') => {
      const normalizedSource = normalizeCurrency(sourceCode) || 'INR';
      const sourceRate = normalizedSource === 'INR' ? 1 : Number(rates?.[normalizedSource]);
      if (effectiveRate == null || !Number.isFinite(sourceRate) || sourceRate <= 0) return 0;
      return (Number(amount) || 0) * (effectiveRate / sourceRate);
    };

    const format = (amount) => {
      if (!cur || effectiveRate == null) return '—';
      const converted = convert(amount);
      const fixed = converted.toFixed(cur.decimals);
      const num = Number(fixed).toLocaleString(cur.locale, {
        minimumFractionDigits: cur.decimals,
        maximumFractionDigits: cur.decimals,
      });
      return `${cur.symbol}${num}`;
    };

    const getBookAmount = (book, field = 'price') => {
      const usdPrice = Number(book?.usdPrice);
      const hasUsdPrice = book?.usdPrice != null && book.usdPrice !== ''
        && Number.isFinite(usdPrice) && usdPrice >= 0;
      if (cur?.code === 'USD' && hasUsdPrice) {
        if (field !== 'originalPrice') return usdPrice;
        // USD selling prices must not be paired with an INR MRP. An absent or
        // invalid USD MRP means there is no advertised discount for this market.
        const mrp = Number(book?.usdOriginalPrice);
        return book?.usdOriginalPrice != null && book.usdOriginalPrice !== ''
          && Number.isFinite(mrp) && mrp >= usdPrice ? mrp : 0;
      }
      // Without a USD selling price, convert both members of the INR pair.
      return effectiveRate == null ? null : Math.round((convert(book?.[field]) + Number.EPSILON) * 100) / 100;
    };

    const formatBookPrice = (book, field = 'price') => {
      const amount = getBookAmount(book, field);
      if (!cur || amount == null) return '—';
      const fixed = amount.toFixed(cur.decimals);
      const num = Number(fixed).toLocaleString(cur.locale, {
        minimumFractionDigits: cur.decimals,
        maximumFractionDigits: cur.decimals,
      });
      return `${cur.symbol}${num}`;
    };

    const formatCurrencyAmount = (amount) => {
      if (!cur) return '—';
      const fixed = (Number(amount) || 0).toFixed(cur.decimals);
      const num = Number(fixed).toLocaleString(cur.locale, {
        minimumFractionDigits: cur.decimals,
        maximumFractionDigits: cur.decimals,
      });
      return `${cur.symbol}${num}`;
    };

    const getBookDiscount = (book) => {
      const price = getBookAmount(book);
      const mrp = getBookAmount(book, 'originalPrice');
      return price != null && mrp != null && Number.isFinite(price) && Number.isFinite(mrp)
        && price >= 0 && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
    };

    return {
      currency: cur?.code || '',
      symbol: cur?.symbol || '',
      rate: effectiveRate,
      convert,
      convertFrom,
      format,
      getBookAmount,
      formatBookPrice,
      formatCurrencyAmount,
      getBookDiscount,
      setCurrency,
      availableCurrencies: Object.keys(currencies),
      currenciesMap: currencies,
      isLiveRate: effectiveRate != null,
    };
  }, [currencyCode, currencies, rates, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within a CurrencyProvider');
  return ctx;
};
